/*
 * WiperPro review marquee.
 *
 * Shared by the homepage review row and the product review row.
 *
 * Markup contract:
 *   .wp-review-marquee
 *     .wp-review-marquee__viewport  <- the scroll container
 *       .wp-review-marquee__track
 *         .wp-review-marquee__group <- exactly ONE group of review cards
 *
 * One engine for every browser and breakpoint: we drift the viewport's scroll
 * position. Nothing depends on CSS percentage transforms or masks, both of which
 * behave differently across engines when applied to a scroll container.
 *
 * The script owns the exact position as a float and only ever WRITES it to the
 * browser. It never reads the position back to work out the next step, because
 * browsers store scroll offsets at differing internal precision and feeding that
 * rounding back in as input compounds into a visibly wrong speed.
 *
 * Desktop: viewport overflow hidden, so the visitor cannot scroll it by hand, but
 *          it is still programmatically scrollable. Hover pauses it.
 * Mobile:  overflow-x auto, so the browser owns swiping and momentum.
 *
 * A second copy of the group is cloned in so the scroller always has content on
 * both sides, and we shift by one group width before the visitor can reach an
 * edge. The copies are identical, so the shift is invisible.
 *
 * Two independent guards keep empty space unreachable:
 *   1. the normal wrap, based on our measured group width, and
 *   2. an edge rescue based on the browser's own reported scroll extents, which
 *      fires even if our measurement is wrong or arrived too early.
 */
(() => {
  // How long the row waits after the visitor stops swiping before it drifts again.
  const IDLE_MS = 1200;
  // Seconds for the row to travel one complete set of reviews. Higher = slower.
  // This is the only knob for scroll speed.
  const LOOP_SECONDS = 200;
  // We know exactly where we put the scroller, so a larger gap than this means a
  // human moved it.
  const USER_SCROLL_EPSILON = 4;

  const mobile = window.matchMedia('(max-width: 749px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const init = (marquee) => {
    if (marquee.dataset.wpMarqueeReady === 'true') return;

    // Fall back to the outer element so an older cached section still works.
    const viewport = marquee.querySelector('.wp-review-marquee__viewport') || marquee;
    const track = marquee.querySelector('.wp-review-marquee__track');
    const firstGroup = marquee.querySelector('.wp-review-marquee__group');
    if (!track || !firstGroup) return;

    marquee.dataset.wpMarqueeReady = 'true';

    let clone = null;
    let groupWidth = 0;
    let loopEnabled = false;
    let inView = false;
    let active = false;
    let hovering = false;
    let rafId = 0;
    let lastFrame = 0;
    let idleUntil = 0;
    // Our authoritative position, in exact floating point.
    let position = 0;
    let expectedScrollLeft = null;
    let lastViewportWidth = window.innerWidth;

    const ensureClone = () => {
      if (clone && clone.isConnected) return;
      clone = firstGroup.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
      track.appendChild(clone);
    };

    const applyScroll = (x) => {
      position = x;
      expectedScrollLeft = x;
      viewport.scrollLeft = x;
    };

    const measure = () => {
      const rect = firstGroup.getBoundingClientRect().width;
      // getBoundingClientRect can report 0 if we are called before layout settles.
      groupWidth = rect > 0 ? rect : firstGroup.scrollWidth;
      loopEnabled =
        groupWidth > 0 &&
        !!clone &&
        clone.isConnected &&
        viewport.clientWidth < groupWidth * 0.5;
      return groupWidth;
    };

    const wrap = (x) => {
      if (x < groupWidth * 0.5) return x + groupWidth;
      if (x > groupWidth * 1.5) return x - groupWidth;
      return x;
    };

    // Park on the second copy. It is identical to the first, so the visitor still
    // sees review one first, but now there is a full group of slack to the left.
    const centre = () => {
      if (!loopEnabled) return;
      applyScroll(groupWidth);
    };

    // On mobile only a horizontal swipe on the cards fires this. Scrolling the page
    // vertically never does, so the row keeps drifting while the visitor reads past.
    const onScroll = () => {
      // If we measured too early, try again now that the row is definitely laid out.
      if (active && !loopEnabled) measure();

      const x = viewport.scrollLeft;
      const movedByVisitor =
        expectedScrollLeft === null || Math.abs(x - expectedScrollLeft) > USER_SCROLL_EPSILON;

      if (movedByVisitor) {
        position = x;
        expectedScrollLeft = x;
        if (mobile.matches) idleUntil = performance.now() + IDLE_MS;
      }

      if (!loopEnabled) return;

      let target = wrap(x);

      // Edge rescue. Trusts the browser's own extents rather than our measurement,
      // so empty space stays unreachable even if groupWidth is wrong.
      const limit = viewport.scrollWidth - viewport.clientWidth;
      if (x <= 1) {
        target = groupWidth;
      } else if (limit > 0 && x >= limit - 1) {
        target = Math.max(0, x - groupWidth);
      }

      if (target !== x) applyScroll(target);
    };

    const shouldAnimate = () =>
      inView && active && loopEnabled && !hovering && !reducedMotion.matches && !document.hidden;

    const animate = (now) => {
      if (!shouldAnimate()) {
        rafId = 0;
        return;
      }

      rafId = requestAnimationFrame(animate);

      const elapsed = lastFrame > 0 ? Math.min(64, now - lastFrame) : 0;
      lastFrame = now;

      if (now < idleUntil) return;

      // Advance our own position. Never read scrollLeft back as the basis for this.
      applyScroll(wrap(position + (groupWidth / LOOP_SECONDS) * (elapsed / 1000)));
    };

    const startLoop = () => {
      if (rafId || !shouldAnimate()) return;
      lastFrame = 0;
      rafId = requestAnimationFrame(animate);
    };

    const stopLoop = () => {
      if (!rafId) return;
      cancelAnimationFrame(rafId);
      rafId = 0;
    };

    const activate = () => {
      if (active) return;
      active = true;
      ensureClone();
      measure();
      marquee.classList.add('is-active');
      centre();
      // Layout can still be settling on first paint, so confirm once more.
      requestAnimationFrame(() => {
        measure();
        if (loopEnabled && viewport.scrollLeft < 1) centre();
        startLoop();
      });
    };

    const relayout = () => {
      if (!active) return;
      measure();
      centre();
      startLoop();
    };

    viewport.addEventListener('scroll', onScroll, { passive: true });

    marquee.addEventListener('mouseenter', () => {
      hovering = true;
      stopLoop();
    }, { passive: true });

    marquee.addEventListener('mouseleave', () => {
      hovering = false;
      startLoop();
    }, { passive: true });

    mobile.addEventListener?.('change', relayout);
    reducedMotion.addEventListener?.('change', startLoop);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopLoop();
      else startLoop();
    });

    // Mobile browsers fire resize when the URL bar collapses mid-scroll. Only react
    // to a genuine width change, otherwise we would yank the visitor's position.
    window.addEventListener('resize', () => {
      if (window.innerWidth === lastViewportWidth) return;
      lastViewportWidth = window.innerWidth;
      relayout();
    }, { passive: true });

    window.addEventListener('orientationchange', relayout, { passive: true });

    window.addEventListener('load', () => {
      if (!active) return;
      const previous = groupWidth;
      measure();
      if (Math.abs(groupWidth - previous) > 1) centre();
      startLoop();
    }, { passive: true });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        inView = entries.some((entry) => entry.isIntersecting);
        if (inView) {
          activate();
          startLoop();
        } else {
          stopLoop();
        }
      }, { rootMargin: '300px 0px' }).observe(marquee);
    } else {
      inView = true;
      activate();
      startLoop();
    }
  };

  const scan = () => {
    document.querySelectorAll('.wp-review-marquee').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', scan);
})();
